/**
 * Read-only bounty detail block (not a routed page). Reads the bounty from
 * router location.state (state.bounty or state.submission.bounty) and shows
 * image (with ImageCarousel lightbox), description, price range, match type
 * and reward via readonly InputField/RangeField components.
 * Composed by BountyViewSubmit and SubheadedBountyDisplay. No API calls.
 */
import React, { useState } from "react";
import { Bounty, bountyTypeDescription } from "@/types/bounty";
import ImageCarousel from "@/components/ImageCarousel";
import InputField, { FieldAccessoryConfig } from "@/components/InputField";
import RangeField from "@/components/RangeField";
import { FieldConfig } from "@/components/InputField";
import { formatPostedDatetime } from "@/utils/bounty";
import { useLocation } from "react-router-dom";
import { FIELD_WIDTH } from "@/constants/ui";

const BountyDisplay: React.FC = () => {
    const location = useLocation();
    const bounty = (location.state.bounty || location.state.submission!.bounty) as Bounty;
    const [showImageCarousel, setShowImageCarousel] = useState(false);
    const descriptionConfig: FieldConfig = {
        key: "description",
        label: "description",
        type: "textarea",
        readonly: true,
        readonlyValue: bounty.description,
        fieldState: null,
        setFieldState: null,
    };

    const matchTypeConfig: FieldConfig = {
        key: "matchType",
        label: "match type",
        type: "text",
        readonly: true,
        readonlyValue: bounty.type,
        fieldState: null,
        setFieldState: null,
    };

    const matchTypeAccessoryConfig: FieldAccessoryConfig = {
        type: "info",
        forType: "text",
        tooltipMessage: bountyTypeDescription(bounty.type, "they"),
    };

    const rewardConfig: FieldConfig = {
        key: "reward",
        label: "reward",
        type: "text",
        readonly: true,
        readonlyValue: `$ ${bounty.reward}`,
        fieldState: null,
        setFieldState: null,
    };

    return (
        <div className="w-full max-w-4xl">
            {showImageCarousel && (
                <ImageCarousel
                    imageUrls={bounty.s3ImageUrls ?? []}
                    onClose={() => setShowImageCarousel(false)}
                />
            )}
            <div className="w-full bg-slate-50 flex flex-col items-center md:flex-row md:items-start md:space-x-8 md:ml-8">
                {/* Image Section */}
                {bounty.s3ImageUrls && bounty.s3ImageUrls.length > 0 && (
                    <div className="flex flex-col mb-4 mt-1">
                        {/* Bounty Image Section */}
                        <div className="relative w-72 h-72 cursor-pointer hover:shadow-xl transition-shadow duration-200 shadow-md rounded-xl">
                            <img
                                src={bounty.s3ImageUrls[0]}
                                alt="bounty photo"
                                className="w-full h-full object-cover rounded-xl"
                                onClick={() => setShowImageCarousel(true)}
                            />
                        </div>
                        <div className="text-sm text-slate-500 mt-2 ml-1">
                            {formatPostedDatetime(bounty.createdAt!)}
                        </div>
                    </div>
                )}
                <div
                    className={`h-full flex flex-col ${FIELD_WIDTH} space-y-2 ${
                        bounty.s3ImageUrls && bounty.s3ImageUrls.length === 0 ? "md:mx-8" : ""
                    }`}
                >
                    {/* Description Text Area */}
                    <InputField
                        key="description"
                        fieldConfig={descriptionConfig}
                        characterLimit={240}
                        height="h-full min-h-[176px]"
                    />
                    {/* Price Range */}
                    <RangeField
                        key="range"
                        label="price range"
                        min={bounty.minPrice}
                        max={bounty.maxPrice}
                        setMin={null}
                        setMax={null}
                        disabled={true}
                    />
                    {!bounty.s3ImageUrls ||
                        (bounty.s3ImageUrls.length === 0 && (
                            <div className="text-sm text-slate-500 ml-1">
                                {formatPostedDatetime(bounty.createdAt!)}
                            </div>
                        ))}
                </div>
                <div className={`h-full flex flex-col ${FIELD_WIDTH} space-y-2`}>
                    {/* Match Type */}
                    <InputField
                        key="matchType"
                        fieldConfig={matchTypeConfig}
                        accessoryConfig={matchTypeAccessoryConfig}
                    />
                    {/* Reward */}
                    <InputField key="reward" fieldConfig={rewardConfig} />
                </div>
            </div>
        </div>
    );
};

export default BountyDisplay;
